
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthenticatedRoute from "./components/AuthenticatedRoute";

// Import Login component directly
import Login from "./pages/Login";
import Account from "./pages/Account";

// Lazily load other components
const Index = lazy(() => import("./pages/Index"));
const Signup = lazy(() => import("./pages/Signup"));
const Pricing = lazy(() => import("./pages/Pricing"));
const LeagueSelection = lazy(() => import("./pages/LeagueSelection"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AuthLanding = lazy(() => import("./pages/AuthLanding"));
const MessageHistory = lazy(() => import("./pages/MessageHistory"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const BetaFull = lazy(() => import("./pages/BetaFull"));
const ConfirmPhone = lazy(() => import("./pages/ConfirmPhone"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Create a Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex justify-center items-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin" />
  </div>
);

// Configure React Query with performance optimizations
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (replacing cacheTime which is renamed to gcTime in v5)
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AnimatePresence mode="wait">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/beta-full" element={<BetaFull />} />
                <Route path="/login" element={<Login />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/account" element={<AuthenticatedRoute><Account /></AuthenticatedRoute>} />
                <Route path="/league-selection" element={<AuthenticatedRoute><LeagueSelection /></AuthenticatedRoute>} />
                <Route path="/message-history" element={<ProtectedRoute><MessageHistory /></ProtectedRoute>} />
                
                {/* Payment processing routes */}
                <Route 
                  path="/payment/success" 
                  element={<PaymentSuccess />}
                />
                <Route path="/payment/cancel" element={<Navigate to="/pricing?payment=cancel" replace />} />
                <Route path="/confirm-phone" element={<AuthenticatedRoute><ConfirmPhone /></AuthenticatedRoute>} />
                
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/auth/callback" element={<AuthLanding />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AnimatePresence>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
